import { useTracking, Options } from 'react-tracking';

import { trackCustomClickEvent, trackSystemEntityEvent, trackPingEvent } from '@bizzabo/entities';
import { instanaReportError } from '@bizzabo/instana-api';

type EventPayload = {
    category?: string;
    description?: string | unknown;
    [key: string]: unknown;
    systemName?: string;
    action?: string;
};

interface SystemInfo {
    trackType: 'System';
    data: EventPayload;
}

interface ClickInfo {
    trackType: 'Click';
    data: EventPayload;
}

interface PingInfo {
    trackType: 'Ping';
    data: EventPayload;
}

export type EventData = SystemInfo | ClickInfo | PingInfo;

const safeStringify = (data: unknown) => {
    try {
        return JSON.stringify(data);
    } catch (e) {
        return '';
    }
};

const eventDispatch = (data: EventData) => {
    if (data.trackType === 'System') {
        if (data.data.systemName) {
            console.warn(`[SP] SystemName ${data.data.systemName}`);
        }
        const category = data.data.systemName || data.data.category;
        trackSystemEntityEvent(
            category,
            safeStringify(data.data.description),
            safeStringify(data.data.description),
            data.data.action
        );
    } else if (data.trackType === 'Click') {
        if (data.data.buttonType) {
            console.warn(`[SP] buttonType ${data.data.buttonType}`);
        }
        data.data.description = safeStringify(data.data.description);

        // TODO add a validate util for analytics
        if ((!data.data.buttonType && !data.data.category) || !data.data.subCategory) {
            instanaReportError({
                error: '[Analytics] Missing field',
                opts: {
                    meta: {
                        buttonType: data.data.buttonType || data.data.category,
                        subCategory: data.data.subCategory,
                        description: data.data.description,
                        action: data.data.action,
                    },
                },
            });
        }
        trackCustomClickEvent({
            ...data.data,
            buttonType: data.data.buttonType || data.data.category,
        });
    } else if (data.trackType === 'Ping') {
        trackPingEvent({
            category: data.data.category,
            description: safeStringify(data.data.description),
            action: data.data.action,
        });
    }
};

export const RingTracking: React.FC = ({ children }) => {
    const { Track } = useTracking({}, { dispatch: eventDispatch });

    return <Track>{children}</Track>;
};

const useTypeTracking = (
    trackingData?: Partial<EventData>,
    options?: Partial<Options<EventData>>
) => {
    const { Track, trackEvent } = useTracking<EventData>(trackingData, options);

    const trackSystemEvent = (data: EventPayload) => {
        trackEvent({
            trackType: 'System',
            data,
        });
    };

    const trackClickEvent = (data: EventPayload) => {
        trackEvent({
            trackType: 'Click',
            data,
        });
    };

    const trackPingEvent = (data: EventPayload) => {
        trackEvent({
            trackType: 'Ping',
            data,
        });
    };

    return { Track, trackEvent, trackSystemEvent, trackClickEvent, trackPingEvent };
};

export default useTypeTracking;
